/**
 * Kontroll's states before there is work to review (#294 ONB-12, ONB-13; owner's correction 2026-11-02):
 * the clock beside the heading, the text and two 44 px links stacked under it on the heading's text
 * edge. One layout for every such state, so they cannot drift apart:
 *
 * - waiting: the first working day has not come; nobody is late before the work has begun
 * - none planned, not started, no app use yet, no registrations yet: why nothing is here, and the way
 *   to record work or bring the source in, without calling anyone missing
 * - unknown: the records could not be read; the count is not zero, it is not known
 *
 * A planned shift that ended with no registration is a «Mangler registrering» row in the list itself
 * (kontroll.ts bucket `gap`), not one of these states. The links only navigate or open a dialog; none
 * completes a step.
 */
import { T } from '@/i18n';
import { dayMonth } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Link } from 'react-router-dom';

export const TIME_GUIDE = 'https://timefly.no/hjelp/leder/timeliste-og-godkjenning.html';

export type StateLink =
  | { label: string; to: string }
  | { label: string; href: string }
  | { label: string; onClick: () => void; disabled?: boolean };

export function WorkState({
  id,
  title,
  text,
  links,
  alert = false,
}: {
  id: string;
  title: string;
  text: string;
  links: StateLink[];
  alert?: boolean;
}) {
  return (
    <section
      className="kwait"
      data-testid={`kontroll-${id}`}
      aria-labelledby={`kwait-${id}`}
      role={alert ? 'alert' : undefined}
    >
      <span className="kwait-ic" aria-hidden="true">
        <Icon name="clock" size={32} strokeWidth={1.8} />
      </span>
      <h2 id={`kwait-${id}`}>{title}</h2>
      <p>{text}</p>
      <div className="kwait-links">
        {links.map((l) =>
          'to' in l ? (
            <Link key={l.label} to={l.to}>
              {l.label}
            </Link>
          ) : 'href' in l ? (
            <a key={l.label} href={l.href} target="_blank" rel="noreferrer">
              {l.label}
            </a>
          ) : (
            <button key={l.label} type="button" onClick={l.onClick} disabled={l.disabled}>
              {l.label}
            </button>
          ),
        )}
      </div>
    </section>
  );
}

export function Waiting({ start }: { start: string }) {
  return (
    <WorkState
      id="waiting"
      title={T('Vi venter på første arbeidsdag')}
      text={T('Dere starter {date}. Ingen timer er forsinket før arbeidet har begynt.', {
        date: dayMonth(start),
      })}
      links={[
        { label: T('Slik registrerer dere tid'), href: TIME_GUIDE },
        { label: T('Til Kom i gang'), to: '/kom-i-gang' },
      ]}
    />
  );
}

/** What Kontroll knows about the company's first work, for {@link firstWorkState}. */
export interface FirstWorkFacts {
  mode: 'rota' | 'time';
  /** people in the company besides the caller, and how many of them have ever logged in */
  people: number;
  loggedIn: number;
  /** published shifts in the period read, and the start of the next one still to come */
  published: number;
  nextPlanned: string | null;
}
export type FirstWorkKind = 'unplanned' | 'not-started' | 'no-app' | 'none-yet';

/**
 * Which state Kontroll shows before the company's first registration (ONB-13). Only for a scope with
 * no record and no gap: a gap is a real missing registration and the list shows it.
 */
export function firstWorkState(f: FirstWorkFacts): FirstWorkKind {
  if (f.mode === 'rota' && f.published === 0) return 'unplanned';
  if (f.mode === 'rota' && f.nextPlanned) return 'not-started';
  if (f.people > 0 && f.loggedIn === 0) return 'no-app';
  return 'none-yet';
}

export function FirstWork({
  kind,
  facts,
  period,
  onAdd,
  mayAdd,
}: {
  kind: FirstWorkKind;
  facts: FirstWorkFacts;
  period: string;
  onAdd: () => void;
  mayAdd: boolean;
}) {
  const add: StateLink = { label: T('Legg til registrering'), onClick: onAdd, disabled: !mayAdd };
  const guide: StateLink = { label: T('Slik registrerer dere tid'), href: TIME_GUIDE };
  if (kind === 'unplanned')
    return (
      <WorkState
        id="unplanned"
        title={T('Ingen arbeid er planlagt i {period}', { period })}
        text={T(
          'Ingen vakter er publisert for perioden, så ingen timer mangler. Er det jobbet likevel, legg det inn.',
        )}
        links={[mayAdd ? add : guide, { label: T('Til vaktplanen'), to: '/vaktplan' }]}
      />
    );
  if (kind === 'not-started')
    return (
      <WorkState
        id="not-started"
        title={T('Arbeidet har ikke begynt ennå')}
        text={T('Første publiserte vakt er {date}. Ingen timer mangler før den.', {
          date: dayMonth((facts.nextPlanned as string).slice(0, 10)),
        })}
        links={[guide, { label: T('Til vaktplanen'), to: '/vaktplan' }]}
      />
    );
  if (kind === 'no-app')
    return (
      <WorkState
        id="no-app"
        title={T('Ingen har tatt i bruk appen ennå')}
        text={T(
          'Stemplinger kommer først når de ansatte har logget inn. Ingen regnes som fraværende av den grunn. Gi dem tilgang, eller legg inn arbeidet selv.',
        )}
        links={[
          { label: T('Gi de ansatte tilgang'), to: '/ansatte?oppgave=ikke-invitert' },
          mayAdd ? add : guide,
        ]}
      />
    );
  return (
    <WorkState
      id="none-yet"
      title={T('Ingen registreringer ennå')}
      text={
        facts.mode === 'time'
          ? T(
              'Uten planlagt arbeid vet ikke Timefly hvem som skulle jobbe, så ingen regnes som manglende. Når de ansatte stempler eller du legger inn arbeid, vises det her.',
            )
          : T('Registrer faktisk arbeid, eller vent på de ansattes registreringer.')
      }
      links={[mayAdd ? add : { label: T('Til Kom i gang'), to: '/kom-i-gang' }, guide]}
    />
  );
}
